import { useEffect, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { useInView, useReducedMotion } from 'motion/react'
import { creator, type PlatformId } from '@/config/creator'
import { PlatformGlyph } from './SocialLinks'

const surfaces: Record<PlatformId, string> = {
  substack: 'bg-secondary text-secondary-foreground md:col-span-6 lg:col-span-5',
  youtube: 'bg-accent text-accent-foreground md:col-span-6 lg:col-span-7',
  linkedin: 'bg-primary text-primary-foreground md:col-span-8 lg:col-span-6',
  x: 'bg-muted text-foreground md:col-span-4 lg:col-span-3',
  instagram: 'bg-secondary text-secondary-foreground md:col-span-6 lg:col-span-3',
  facebook: 'bg-card text-card-foreground md:col-span-6 lg:col-span-6',
  threads: 'bg-accent/30 text-foreground md:col-span-12 lg:col-span-6',
}

function FollowerCount({ value }: { value: number }) {
  const ref = useRef<HTMLSpanElement>(null)
  const visible = useInView(ref, { once: true })
  const reducedMotion = useReducedMotion()
  const [count, setCount] = useState(value)
  useEffect(() => {
    if (!visible || reducedMotion) { setCount(value); return }
    let frame = 0
    let start: number | undefined
    const tick = (time: number) => {
      start ??= time
      const progress = Math.min((time - start) / 900, 1)
      setCount(Math.round(value * (1 - Math.pow(1 - progress, 3))))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [visible, reducedMotion, value])
  return <span ref={ref}><span aria-hidden="true">{count.toLocaleString('en-US')} followers</span><span className="sr-only">{value.toLocaleString('en-US')} followers</span></span>
}

export default function PlatformCards() {
  return (
    <section id="everywhere" aria-labelledby="everywhere-heading" className="creator-section bg-background">
      <div className="creator-container">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div><p className="creator-label mb-4 text-primary">Find me everywhere</p><h2 id="everywhere-heading" className="creator-display max-w-3xl text-[clamp(2.5rem,5vw,4.5rem)]">One person.<br />Many open tabs.</h2></div>
          <p className="max-w-xs text-base leading-relaxed text-muted-foreground">I share what I’m learning, what I’m building, and the questions that keep me curious.</p>
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-12">
          {creator.socials.map(profile => {
            const large = ['substack', 'youtube', 'linkedin'].includes(profile.id)
            const content = <>
              <div className="flex items-start justify-between gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-current/20"><PlatformGlyph id={profile.id} className="h-7 w-7" /></span>
                {profile.url ? <ArrowUpRight aria-hidden="true" className="h-6 w-6" /> : <span className="rounded-full border border-current/25 px-3 py-1 text-xs font-medium">Profile coming soon</span>}
              </div>
              <div className={large ? 'mt-10' : 'mt-7'}>
                <h3 className={`creator-display ${large ? 'text-4xl md:text-5xl' : 'text-3xl'}`}>{profile.name}</h3>
                {profile.handle && <p className="mt-2 break-words text-sm font-medium">{profile.handle}</p>}
                <p className="mt-4 max-w-md text-base leading-relaxed">{profile.description}</p>
              </div>
              <div className="mt-8 flex min-h-11 flex-wrap items-center justify-between gap-3 border-t border-current/20 pt-4 text-sm font-semibold">
                {profile.followers !== null && Number.isFinite(profile.followers) && profile.followers >= 0 ? <FollowerCount value={profile.followers} /> : <span>{profile.url ? 'Join the conversation' : 'Profile coming soon'}</span>}
                {profile.url && <span className="flex items-center gap-2">Visit {profile.name}<ArrowUpRight aria-hidden="true" size={16} /></span>}
              </div>
            </>
            const className = `bento-card flex h-full flex-col justify-between p-6 md:p-8 ${surfaces[profile.id]}`
            return profile.url ? <a id={`platform-${profile.id}`} key={profile.id} href={profile.url} target="_blank" rel="noopener noreferrer" aria-label={`Visit my ${profile.name} profile — opens in a new tab`} className={className}>{content}</a> : <article id={`platform-${profile.id}`} key={profile.id} aria-label={`${profile.name}: profile link is not available yet`} className={className}>{content}</article>
          })}
        </div>
      </div>
    </section>
  )
}
